import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Facebook, Instagram, Phone, Mail, MapPin, MessageCircle } from 'lucide-react';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const safeParse = (str, fallback) => { try { return JSON.parse(str) || fallback; } catch { return fallback; } };

const Footer = () => {
  const { settings } = useSiteSettings();
  const [courseTypes, setCourseTypes] = useState([]);
  const s = settings || {};

  useEffect(() => {
    axios.get(`${API_URL}/categories?type=course_type`)
      .then(res => setCourseTypes(res.data || []))
      .catch(() => {});
  }, []);

  const phone = s.phone || '';
  const email = s.email || '';
  const address = s.address || '';
  const whatsapp = s.whatsapp || '';
  const facebookUrl = s.facebook_url || '';
  const instagramUrl = s.instagram_url || '';
  const companyName = s.company_name || '智駕駛';

  const footerLinksTitle = s.footer_links_title || '快速連結';
  const footerCoursesTitle = s.footer_courses_title || '課程類別';
  const footerContactTitle = s.footer_contact_title || '聯絡資訊';
  const showPhone = s.footer_show_phone !== false;
  const showWhatsapp = s.footer_show_whatsapp === true;
  const showEmail = s.footer_show_email !== false;
  const showAddress = s.footer_show_address !== false;

  // Use stored links if set; otherwise fall back to navbar / course categories
  const storedCol2 = safeParse(s.footer_col2_links, []);
  const storedCol3 = safeParse(s.footer_col3_links, []);
  const navItems = safeParse(s.navbar_items, []);
  const col2Links = storedCol2.length > 0 ? storedCol2 : navItems.map(n => ({ label: n.label, url: n.path }));
  const col3Links = storedCol3.length > 0 ? storedCol3 : courseTypes.map(c => ({ label: c.name, url: '/courses' }));

  const renderLink = (item, i) => (
    <li key={i}>
      {item.url?.startsWith('http') ? (
        <a href={item.url} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">{item.label}</a>
      ) : (
        <Link to={item.url || '/'} className="hover:text-primary transition-colors">{item.label}</Link>
      )}
    </li>
  );

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">

          {/* Col 1: Company */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4">{companyName}</h3>
            <p className="text-sm mb-4">
              {s.about_text || '提供專業駕駛培訓課程，經驗豐富的導師團隊，協助您順利考取駕駛執照。'}
            </p>
            {(facebookUrl || instagramUrl) && (
              <div className="flex space-x-4">
                {facebookUrl && (
                  <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                    <Facebook className="h-5 w-5" />
                  </a>
                )}
                {instagramUrl && (
                  <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                    <Instagram className="h-5 w-5" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Col 2: Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">{footerLinksTitle}</h4>
            <ul className="space-y-2 text-sm">{col2Links.map(renderLink)}</ul>
          </div>

          {/* Col 3: Course Categories */}
          <div>
            <h4 className="text-white font-semibold mb-4">{footerCoursesTitle}</h4>
            <ul className="space-y-2 text-sm">{col3Links.map(renderLink)}</ul>
          </div>

          {/* Col 4: Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">{footerContactTitle}</h4>
            <ul className="space-y-3 text-sm">
              {showPhone && phone && (
                <li className="flex items-start">
                  <Phone className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{phone}</span>
                </li>
              )}
              {showWhatsapp && whatsapp && (
                <li className="flex items-start">
                  <MessageCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                  <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">WhatsApp</a>
                </li>
              )}
              {showEmail && email && (
                <li className="flex items-start">
                  <Mail className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{email}</span>
                </li>
              )}
              {showAddress && address && (
                <li className="flex items-start">
                  <MapPin className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{address}</span>
                </li>
              )}
            </ul>
          </div>

        </div>
        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm">
          <p>&copy; {new Date().getFullYear()} {companyName} Wise Driving. 版權所有。</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
